import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiUrl } from '../lib/apiBase';

const SESSION_KEY = 'tracttrace.visit.pinged';

function collectUtm(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const utm: Record<string, string> = {};
  for (const key of [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ]) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }
  return utm;
}

function collectClientMeta(pathname: string, search: string) {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const win = typeof window !== 'undefined' ? window : null;
  const connection =
    nav && 'connection' in nav
      ? (nav as Navigator & { connection?: { effectiveType?: string } }).connection
          ?.effectiveType
      : undefined;
  const deviceMemory =
    nav && 'deviceMemory' in nav
      ? (nav as Navigator & { deviceMemory?: number }).deviceMemory
      : undefined;

  return {
    path: `${pathname}${search}`,
    url: win?.location.href ?? '',
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    language: nav?.language || '',
    languages: nav?.languages?.slice(0, 6).join(', ') || '',
    screenResolution: win ? `${win.screen.width}x${win.screen.height}` : '',
    viewport: win ? `${win.innerWidth}x${win.innerHeight}` : '',
    colorDepth: win?.screen.colorDepth,
    pixelRatio: win?.devicePixelRatio,
    platform: nav?.platform || '',
    touchPoints: nav?.maxTouchPoints ?? 0,
    hardwareConcurrency: nav?.hardwareConcurrency,
    deviceMemoryGb: deviceMemory,
    connectionType: connection || '',
    utm: collectUtm(search),
  };
}

/**
 * Once per browser session, ping the backend so Discord gets a visit alert
 * with device / geo / referrer metadata. Failures are silent.
 */
export function VisitTracker() {
  const location = useLocation();

  useEffect(() => {
    // Skip admin console noise
    if (location.pathname.startsWith('/admin')) return;

    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // sessionStorage blocked — still attempt one beacon this mount
    }

    const payload = collectClientMeta(location.pathname, location.search);
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8_000);

    void fetch(apiUrl('/api/v1/visit'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
      keepalive: true,
    }).catch(() => {
      // ignore network / abort
    }).finally(() => {
      window.clearTimeout(timer);
    });
  }, [location.pathname, location.search]);

  return null;
}
